import React, { useState, useEffect } from "react";
import { qcClient, QCJobResult, ApiCall } from "../../api/qc_client";
import QCJobSubmitter from "./QCJobSubmitter";
import QCJobViewer from "./QCJobViewer";
import ApiInspector from "./ApiInspector";
import "./QCDashboard.css";

interface QCDashboardProps {
  /** Auth token from useAuth — auto-injected into the QC client */
  authToken?: string | null;
}

const QCDashboard: React.FC<QCDashboardProps> = ({ authToken }) => {
  const [activeTab, setActiveTab] = useState<"submit" | "history" | "inspector">("submit");
  const [submittedJob, setSubmittedJob] = useState<QCJobResult | null>(null);
  const [jobHistory, setJobHistory] = useState<QCJobResult[]>([]);
  const [apiCalls, setApiCalls] = useState<ApiCall[]>([]);
  const [health, setHealth] = useState<string>("checking...");
  const [token, setToken] = useState<string>("");

  // Sync auth token from useAuth into the QC client
  useEffect(() => {
    if (authToken) {
      qcClient.setToken(authToken);
      setToken(authToken);
    }
  }, [authToken]);

  useEffect(() => {
    // Set up API call logging
    qcClient.setApiCallListener((call) => {
      setApiCalls((prev) => [...prev, call]);
    });

    // Check health
    checkHealth();

    return () => {
      qcClient.setApiCallListener(undefined);
    };
  }, []);

  const checkHealth = async () => {
    try {
      const response = await qcClient.healthCheck();
      setHealth(`✅ OK (mongo: ${response.mongo}, rag_db: ${response.rag_db})`);
    } catch (error) {
      setHealth(`❌ Failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleTokenChange = (newToken: string) => {
    setToken(newToken);
    qcClient.setToken(newToken);
  };

  const handleJobSubmitted = (job: QCJobResult) => {
    setSubmittedJob(job);
    setJobHistory((prev) => [job, ...prev]);
    setActiveTab("submit");
  };

  const handleClearHistory = () => {
    setJobHistory([]);
    qcClient.clearApiCalls();
    setApiCalls([]);
  };

  return (
    <div className="qc-dashboard">
      <header className="qc-header">
        <h1>🎬 QC Engine Demo Dashboard</h1>
        <div className="header-controls">
          <div className="health-status">
            Health: {health}
            <button onClick={checkHealth} className="btn-small">
              Refresh
            </button>
          </div>
          <div className="token-input">
            <label>Bearer Token (optional):</label>
            <input
              type="password"
              value={token}
              onChange={(e) => handleTokenChange(e.target.value)}
              placeholder="Leave empty for demo mode"
              className="input-field"
            />
          </div>
        </div>
      </header>

      <nav className="qc-tabs">
        <button
          className={`tab ${activeTab === "submit" ? "active" : ""}`}
          onClick={() => setActiveTab("submit")}
        >
          📝 Submit Job
        </button>
        <button
          className={`tab ${activeTab === "history" ? "active" : ""}`}
          onClick={() => setActiveTab("history")}
        >
          📋 History ({jobHistory.length})
        </button>
        <button
          className={`tab ${activeTab === "inspector" ? "active" : ""}`}
          onClick={() => setActiveTab("inspector")}
        >
          🔍 API Inspector ({apiCalls.length})
        </button>
      </nav>

      <main className="qc-content">
        {activeTab === "submit" && (
          <div className="tab-content">
            <QCJobSubmitter onJobSubmitted={handleJobSubmitted} />
            {submittedJob && (
              <div className="current-job">
                <h2>Current Job</h2>
                <QCJobViewer job={submittedJob} />
              </div>
            )}
          </div>
        )}

        {activeTab === "history" && (
          <div className="tab-content">
            <div className="history-header">
              <h2>Job History</h2>
              <button onClick={handleClearHistory} className="btn-danger">
                Clear All
              </button>
            </div>
            <div className="job-history">
              {jobHistory.length === 0 ? (
                <p className="empty-state">No jobs submitted yet</p>
              ) : (
                jobHistory.map((job) => (
                  <div key={job.job_id} className="history-item">
                    <div className="history-item-header">
                      <span className="job-id">{job.job_id}</span>
                      <span className={`status status-${job.status}`}>{job.status}</span>
                      {job.qc_status && (
                        <span className={`qc-status qc-${job.qc_status}`}>{job.qc_status}</span>
                      )}
                      <span className="asset-name">{job.asset_name}</span>
                      {job.processing_time_ms && (
                        <span className="timing">{job.processing_time_ms}ms</span>
                      )}
                    </div>
                    <div className="history-item-details">
                      <details>
                        <summary>View Details</summary>
                        <QCJobViewer job={job} compact />
                      </details>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === "inspector" && <ApiInspector calls={apiCalls} />}
      </main>
    </div>
  );
};

export default QCDashboard;
